import { useState, useRef, useCallback, useEffect } from 'react';
import mqtt from 'mqtt';

const MAX_MESSAGES = 50;
const CONNECT_TIMEOUT_MS = 10_000;
const AUTO_DISCONNECT_MS = 30_000;

const useMqttTest = () => {
  const [status, setStatus] = useState('idle');
  const [error, setError] = useState(null);
  const [messages, setMessages] = useState([]);

  const clientRef = useRef(null);
  const timeoutsRef = useRef({});
  const connectedRef = useRef(false);

  const cleanup = useCallback(() => {
    clearTimeout(timeoutsRef.current.connect);
    clearTimeout(timeoutsRef.current.autoDisconnect);
    timeoutsRef.current = {};
    connectedRef.current = false;

    if (clientRef.current) {
      clientRef.current.end(true);
      clientRef.current = null;
    }
  }, []);

  const stopTest = useCallback(() => {
    cleanup();
    setStatus((prev) => (prev === 'error' ? 'error' : 'idle'));
  }, [cleanup]);

  const startTest = useCallback((mqttConfig) => {
    cleanup();
    setMessages([]);
    setError(null);
    setStatus('connecting');
    connectedRef.current = false;

    const prefix = (mqttConfig.topicPrefix || '').replace(/\/+$/, '');
    const topic = !prefix
      ? '#'
      : prefix.endsWith('#')
        ? prefix
        : `${prefix}/#`;

    const client = mqtt.connect(mqttConfig.broker, {
      username: mqttConfig.username || undefined,
      password: mqttConfig.password || undefined,
      rejectUnauthorized: false,
      connectTimeout: CONNECT_TIMEOUT_MS,
      reconnectPeriod: 0,
      clean: true,
      clientId: `vtec_test_${Date.now()}`,
      protocolVersion: 4,
    });

    clientRef.current = client;

    timeoutsRef.current.connect = setTimeout(() => {
      if (clientRef.current && !connectedRef.current) {
        setError('Connection timed out after 10 seconds');
        setStatus('error');
        cleanup();
      }
    }, CONNECT_TIMEOUT_MS);

    client.on('connect', () => {
      clearTimeout(timeoutsRef.current.connect);
      connectedRef.current = true;
      setStatus('connected');

      client.subscribe(topic, (err) => {
        if (err) {
          setError(`Subscribe failed: ${err.message}`);
          setStatus('error');
          cleanup();
        }
      });

      timeoutsRef.current.autoDisconnect = setTimeout(() => {
        stopTest();
      }, AUTO_DISCONNECT_MS);
    });

    client.on('message', (receivedTopic, payload) => {
      let parsed;
      try {
        const str = payload.toString();
        try {
          parsed = JSON.parse(str);
        } catch {
          parsed = str;
        }
      } catch {
        parsed = '(binary data)';
      }

      setMessages((prev) => {
        if (prev.length >= MAX_MESSAGES) return prev;
        return [...prev, {
          topic: receivedTopic,
          payload: typeof parsed === 'object' ? JSON.stringify(parsed, null, 2) : String(parsed),
          timestamp: new Date(),
        }];
      });
    });

    client.on('error', (err) => {
      clearTimeout(timeoutsRef.current.connect);
      setError(err?.message || 'Connection failed');
      setStatus('error');
      cleanup();
    });

    client.on('close', () => {
      if (clientRef.current && connectedRef.current) {
        setError('Connection lost');
        setStatus('error');
        cleanup();
      }
    });
  }, [cleanup, stopTest]);

  useEffect(() => {
    return cleanup;
  }, [cleanup]);

  return { status, error, messages, startTest, stopTest };
};

export default useMqttTest;
